import { type NextPageContext } from 'next'
import { useRouter } from 'next/router'
import { observer, inject } from 'mobx-react'
import Deals from 'static/pages/Deals'
import CustomHead from '../../components/CustomHead'
import type { LanguageCode } from 'utils/utils'
import { getLocaleStr, codeToLocale, sendRequest, safeStringify } from 'utils/utils'
import { CACHE_POLICY } from '../../utils/constants'

interface DealsData {
	banners?: any[]
	offers?: any[]
	ltos?: {
		delivery?: any[]
		pickup?: any[]
	}
}

interface DealsProps {
	deals: DealsData
	isGeneratedStatically: boolean
	footer: any
	params: any
	lang: string
	conversationText: any
	history: any
}

const DealsPage = inject(
	'Infra',
	'User'
)(
	observer(({ Infra, User, data, lang }: { Infra: any; User: any; data: { deals: DealsData }; lang: LanguageCode }) => {
		const router = useRouter()

		const dealsProps: DealsProps = {
			deals: data.deals,
			lang: User.preferredLanguage || Infra.appParams.l,
			conversationText: User.userLocaleLanguage,
			footer: Infra.eCommerceFooter,
			isGeneratedStatically: true,
			params: Infra.appParams,
			history: router,
		}
		const title = getLocaleStr(Infra.appParams?.pageText?.deals?.title, codeToLocale[lang] || 'en_US')
		const description = getLocaleStr(Infra.appParams?.pageText?.deals?.description, codeToLocale[lang] || 'en_US')

		// Remove 'ref' from appParams because it throws an error on CustomHead (string ref prop not allowed on function component, see https://legacy.reactjs.org/docs/refs-and-the-dom.html#legacy-api-string-refs)
		const { ref, ...appParamsWithoutRef } = Infra.appParams

		return (
			<>
				<CustomHead {...appParamsWithoutRef} title={title} description={description} lang={lang} key={1} />
				<Deals {...dealsProps} />
			</>
		)
	})
)

export async function getServerSideProps(context: NextPageContext & { req: { extra: { appParams: any; nextJsError: string } } }) {
	if (context.res) {
		context.res.setHeader(
			'Cache-Control',
			`public, s-maxage=${CACHE_POLICY.DEFAULT.MAX_AGE}, stale-while-revalidate=${CACHE_POLICY.DEFAULT.REVALIDATED_AGE}`
		)
	}

	if (context.req && !context.req.extra?.nextJsError) {
		try {
			const appParams = context.req.extra?.appParams
			const chainId = appParams.c || appParams.pc
			const lang = appParams.l
			const wruec = process.env.ECOMMERCE_SERVER
			// fetch deals data
			const deals = await (sendRequest(false, `${wruec}/v1/tenants/${chainId}/deals2?lang=${lang}`, 'get', null, null) as Promise<DealsData>)

			return {
				props: {
					data: { 
						deals: {
							...deals,
							banners: [],
							offers: []
						},
					},
					lang,
				},
			}
		} catch (e: any) {
			console.error(safeStringify(e))
		}
	}
  if (context.res) {
    context.res.statusCode = 500
  }

	return { props: { data: { deals: null } } }
}

export default DealsPage
